import type { CadsBadgeBitSemanticColor } from '../cads-badge-bit/cads-badge-bit.config';

/** Typography scale for {@link CadsAmountDetailBitConfig}. */
export type CadsAmountDetailBitSize = 'small' | 'medium' | 'large';

/**
 * Semantic tone for the optional subtext line. **`muted`** maps to **`--cads-color-text-muted`**; other keys map to
 * **`--cads-color-{key}`** from the CADS palette.
 */
export type CadsAmountDetailBitSubtextTone = 'muted' | CadsBadgeBitSemanticColor;

/**
 * Configuration for {@link CadsAmountDetailBitComponent}. Pass numeric **`value`**; formatting and sign rules use
 * {@link CadsCreditValueStyle}. Host supplies all visible strings (no i18n inside CADS v1).
 */
export interface CadsAmountDetailBitConfig {
  /** Amount in dollars; displayed with two fraction digits. */
  readonly value: number;
  /** Optional second line (e.g. status or label). */
  readonly subtext?: string;
  /**
   * Color tone for {@link CadsAmountDetailBitConfig.subtext}. Default **`muted`** when subtext is set.
   */
  readonly subtextTone?: CadsAmountDetailBitSubtextTone;
  /**
   * Controls sign prefixes and green emphasis via {@link CadsCreditValueStyle}:
   * - `null` / omit: neutral (no + or − prefix; absolute figures; no green).
   * - `true`: credit-card context (negative amounts: leading minus and green; positive: plain).
   * - `false`: deposit / bank-account context (positive: green; negative: red with leading minus).
   */
  readonly isCredit?: boolean | null;
  /** Typography scale. Default **`medium`**. */
  readonly size?: CadsAmountDetailBitSize;
}

/**
 * Resolves {@link CadsAmountDetailBitSubtextTone} to a CSS **`color`** for templates.
 */
export function cadsAmountDetailSubtextColorCss(tone: CadsAmountDetailBitSubtextTone): string {
  if (tone === 'muted') {
    return 'var(--cads-color-text-muted)';
  }
  return `var(--cads-color-${tone})`;
}
